import type { MonitoringInvestigationContext } from "../../../shared/chatInvestigation";
import { AskMonitoringChat } from "./AskMonitoringChat";
import type { WindowOption } from "./monitoringWindow";
import { styled } from "goober";
import { Text } from "../Text";
import { AiProviderModelSpendChart } from "./AiProviderModelSpendChart";
import type { AiProviderModelCostRow } from "./aiProviderMonitoring";
import { formatEstimatedCostUsd } from "./aiUsage";
import { formatModelName } from "../../lib/formatModelName";
import {
  Bar,
  BarColumn,
  BarRow,
  ColumnMeta,
  FadeScrollColumns,
} from "./monitoringBars";
import {
  MONITORING_SHORT_VIEWPORT_HEIGHT,
  SPACE_COLORS,
} from "./monitoringPanelLayout";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { Panel, PanelHeader, TotalRow } from "./workflowsLayout";

// In the collapsed modes (single row when short, single column when narrow) the
// panel gets a tall card to fill, so it flips from the compact stacked bar to
// vertical bars — like Executions by space. The normal layout, where the
// panel hugs its content, keeps the stacked bar + legend.
const COLLAPSED = `@media (max-width: 900px), (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px)`;

const VerticalOnly = styled("div")`
  display: none;
  ${COLLAPSED} {
    display: flex;
    flex: 1;
    min-height: 0;
  }
`;

const StackedOnly = styled("div")`
  margin-top: auto;
  ${COLLAPSED} {
    display: none;
  }
`;

type Props = {
  modelRows: readonly AiProviderModelCostRow[];
  lookback: WindowOption;
  investigation: MonitoringInvestigationContext;
  loading: boolean;
};

export function AiSpendByModelPanel({
  modelRows,
  lookback,
  investigation,
  loading,
}: Props) {
  const total = modelRows.reduce(
    (sum, row) => sum + (row.estimatedCostUsd ?? 0),
    0
  );
  const maxSpend = Math.max(
    0,
    ...modelRows.map((r) => r.estimatedCostUsd ?? 0)
  );
  const isEmpty = modelRows.length === 0;

  const emptyText = (
    <Text variant="text-small" color="var(--text-secondary)">
      No usage recorded {lookback.phrase}.
    </Text>
  );

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>{lookback.label}</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Spend by model</Text>
        <TotalRow>
          <Text variant="display-md">{formatEstimatedCostUsd(total)}</Text>
        </TotalRow>
      </PanelHeader>

      <VerticalOnly>
        {isEmpty ? (
          emptyText
        ) : (
          <FadeScrollColumns>
            {modelRows.map((row, i) => {
              const spend = row.estimatedCostUsd ?? 0;
              return (
                <BarColumn key={row.model} data-bar-column>
                  <BarRow>
                    <Bar
                      data-bar
                      style={{
                        background: SPACE_COLORS[i % SPACE_COLORS.length]!,
                        minHeight: spend > 0 ? 6 : 0,
                        height: `${maxSpend > 0 ? (spend / maxSpend) * 100 : 0}%`,
                      }}
                    />
                  </BarRow>
                  <ColumnMeta>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {formatModelName(row.model)}
                    </Text>
                    <Text
                      variant="text-small-md"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {formatEstimatedCostUsd(spend)}
                    </Text>
                  </ColumnMeta>
                </BarColumn>
              );
            })}
          </FadeScrollColumns>
        )}
      </VerticalOnly>

      <StackedOnly>
        {isEmpty ? (
          emptyText
        ) : (
          <AiProviderModelSpendChart modelRows={modelRows} />
        )}
      </StackedOnly>
      <AskMonitoringChat
        disabled={loading}
        surface="ai-models"
        context={investigation}
        snapshot={`${formatEstimatedCostUsd(total)} estimated spend across ${modelRows.length} models`}
      />
    </Panel>
  );
}
